class ProductSizeSelector extends HTMLElement {
  constructor() {
    super();
    this.analyzeProductSelectors();
    this.initializeSelectors();
    this.initializeProductData();
    this.initializeEventListeners();
  }

  analyzeProductSelectors() {
    // Get product type and configuration from data attributes
    this.hasHeight = this.dataset.hasHeight === 'true';
    this.isSheet = this.dataset.isSheet === 'true';
    this.productId = this.dataset.productId;
    this.isCustomSize = this.dataset.isCustomSize === 'true';

    console.log('Product Configuration:', {
      hasHeight: this.hasHeight,
      isSheet: this.isSheet,
      productId: this.productId,
      isCustomSize: this.isCustomSize
    });

    // Find all selector groups
    const selectorGroups = this.querySelectorAll('.product-size-selector__group');
    console.log('Found selector groups:', selectorGroups.length);

    // Analyze each selector group
    this.selectors = {
      width: this.querySelector('[data-width-select]'),
      length: this.querySelector('[data-length-select]'),
      height: this.querySelector('[data-height-select]'),
      color: this.querySelector('[data-color-select]')
    };

    // Log available selectors and their options
    Object.entries(this.selectors).forEach(([type, selector]) => {
      if (selector) {
        const options = Array.from(selector.options)
          .filter(option => option.value) // Filter out placeholder options
          .map(option => ({
            value: option.value,
            text: option.text,
            dataRange: option.dataset.range
          }));

        console.log(`${type} selector:`, {
          id: selector.id,
          options: options,
          hasDataRange: !!options.find(opt => opt.dataRange)
        });
      }
    });

    // Find variant selects component
    this.variantSelects = document.querySelector('variant-selects');
    if (this.variantSelects) {
      // Parse variant data
      const variantScript = this.variantSelects.querySelector('script[type="application/json"]');
      if (variantScript) {
        try {
          const variants = JSON.parse(variantScript.textContent);
          console.log('Variant Structure:', {
            count: variants.length,
            sampleVariant: variants[0],
            hasRanges: variants[0].option1.includes('-'),
            hasSeparator: variants[0].option1.includes('|')
          });
        } catch (error) {
          console.error('Error parsing variants:', error);
        }
      }

      // Check for color options
      const colorOptions = this.variantSelects.querySelectorAll('.variant-input-wrapper[data-option-color]');
      console.log('Color options found:', colorOptions.length > 0);
    }

    // Determine product type based on ID and selectors
    this.determineProductType();
  }

  determineProductType() {
    // Product IDs from the template
    const PRODUCT_TYPES = {
      MATTRESS_GROUP_1: ['15003998552224', '15004012740768', '15004012806304'], // No.3, No.5, No.7
      MATTRESS_GROUP_2: ['15004555706528', '15004556460192', '15004556886176'], // No.9, No.11, No.13
      PROTECTOR: ['15006027382944'],
      SHEETS: ['15006023057568','15006024630432']
    };

    let productType = 'unknown';
    
    if (PRODUCT_TYPES.MATTRESS_GROUP_1.includes(this.productId)) {
      productType = 'mattress-group-1';
    } else if (PRODUCT_TYPES.MATTRESS_GROUP_2.includes(this.productId)) {
      productType = 'mattress-group-2';
    } else if (PRODUCT_TYPES.PROTECTOR.includes(this.productId)) {
      productType = 'protector';
    } else if (PRODUCT_TYPES.SHEETS.includes(this.productId)) {
      productType = 'sheets';
    }

    console.log('Determined product type:', {
      type: productType,
      expectedSelectors: this.getExpectedSelectors(productType)
    });

    return productType;
  }

  getExpectedSelectors(productType) {
    const selectorMap = {
      'mattress-group-1': ['width', 'length', 'height'],
      'mattress-group-2': ['width', 'length', 'height'],
      'protector': ['width', 'length', 'height'],
      'sheets': ['width', 'length', 'height', 'color'],
      'unknown': ['width']
    };

    return selectorMap[productType] || selectorMap.unknown;
  }

  setupAddToCartValidation() {
    // Find the add to cart button and form
    this.addToCartBtn = document.querySelector('#AddToCart, [name="add"]');
    this.productForm = this.addToCartBtn?.closest('form');

    if (this.addToCartBtn) {
      // Initial validation
      this.validateSelections();

      // Add form submit handler
      if (this.productForm) {
        this.productForm.addEventListener('submit', (e) => {
          if (!this.validateSelections()) {
            e.preventDefault();
            this.showValidationMessage();
          }
        });
      }
    }
  }

  validateSelections() {
    if (!this.requiredSelectors || !this.selectors) return true;

    const invalidSelectors = this.requiredSelectors.filter(selectorType => {
      const selector = this.selectors[selectorType];
      return selector && (!selector.value || selector.value === '');
    });

    const isValid = invalidSelectors.length === 0;
    
    // Update button state
    if (this.addToCartBtn) {
      if (isValid) {
        this.addToCartBtn.removeAttribute('disabled');
        this.removeValidationMessage();
      } else {
        this.addToCartBtn.setAttribute('disabled', 'disabled');
      }
    }

    console.log('Validation result:', {
      isValid,
      invalidSelectors,
      requiredSelectors: this.requiredSelectors
    });

    return isValid;
  }

  showValidationMessage() {
    if (!this.addToCartBtn) return;

    // Remove any existing message
    this.removeValidationMessage();

    // Create validation message
    const message = document.createElement('div');
    message.className = 'validation-message';
    message.style.color = 'red';
    message.style.marginTop = '10px';
    message.style.fontSize = '14px';
    
    const invalidSelectors = this.requiredSelectors.filter(selectorType => {
      const selector = this.selectors[selectorType];
      return selector && (!selector.value || selector.value === '');
    });

    message.textContent = `Please select ${invalidSelectors.join(', ')} before adding to cart.`;
    
    // Insert message after add to cart button
    this.addToCartBtn.parentNode.insertBefore(message, this.addToCartBtn.nextSibling);
  }

  removeValidationMessage() {
    const existingMessage = this.addToCartBtn?.parentNode.querySelector('.validation-message');
    if (existingMessage) {
      existingMessage.remove();
    }
  }

  initializeSelectors() {
    this.widthSelect = this.querySelector('[data-width-select]');
    this.heightSelect = this.querySelector('[data-height-select]');
    this.lengthSelect = this.querySelector('[data-length-select]');
    this.variantSelects = document.querySelector('variant-selects');
    this.radioInputs = this.variantSelects?.querySelectorAll('input[type="radio"]');
    this.addToCartBtn = document.querySelector('#AddToCart');
  }

  initializeProductData() {
    try {
      const scriptTag = this.variantSelects?.querySelector('script[type="application/json"]');
      if (scriptTag) {
        this.variants = JSON.parse(scriptTag.textContent);
        this.isSheetProduct = !this.variants[0].option1.includes('|');
        console.log('Product type:', this.isSheetProduct ? 'Sheet' : 'Mattress');
      } else {
        console.error('Variant data not found');
      }
    } catch (error) {
      console.error('Error parsing variant data:', error);
    }
  }

  initializeEventListeners() {
    // Add validation check to all selector change events
    Object.values(this.selectors).forEach(selector => {
      if (selector) {
        selector.addEventListener('change', () => {
          this.updateSelectedVariant();
          this.validateSelections();
        });
      }
    });
  }

  updateSelectedVariant() {
    const selectedWidth = parseInt(this.widthSelect?.value) || 0;
    const selectedHeight = parseInt(this.heightSelect?.value) || 0;
    
    console.log('Updating variant with:', {
      width: selectedWidth,
      height: selectedHeight,
      isSheetProduct: this.isSheetProduct
    });
    
    const matchingVariant = this.findMatchingVariant(selectedWidth, selectedHeight);
    
    if (matchingVariant) {
      console.log('Found matching variant:', matchingVariant);
      this.selectVariantInDOM(matchingVariant);
    } else {
      console.log('No matching variant found');
    }
  }

  findMatchingVariant(width, height) {
    if (this.isSheetProduct) {
      return this.variants.find(variant => {
        const [minWidth, maxWidth] = variant.option1.split('-').map(n => parseInt(n));
        const matches = width >= minWidth && width <= maxWidth;
        
        console.log('Checking sheet variant:', {
          option1: variant.option1,
          option2: variant.option2,
          width: width,
          range: `${minWidth}-${maxWidth}`,
          matches: matches
        });
        
        return matches;
      });
    } else {
      return this.variants.find(variant => {
        const [widthRange, heightValue] = variant.option1.split('|').map(part => part.trim());
        const [minWidth, maxWidth] = widthRange.split('-').map(n => parseInt(n));
        const variantHeight = parseInt(heightValue);
        
        const matches = width >= minWidth && 
                       width <= maxWidth && 
                       (!height || variantHeight === height);
        
        console.log('Checking mattress variant:', {
          option1: variant.option1,
          width: width,
          height: height,
          range: `${minWidth}-${maxWidth}`,
          variantHeight: variantHeight,
          matches: matches
        });
        
        return matches;
      });
    }
  }

  selectVariantInDOM(variant) {
    const radioInput = Array.from(this.radioInputs).find(input => 
      input.value === variant.option1
    );
    
    if (radioInput) {
      console.log('Selecting variant in DOM:', variant.option1);
      
      // this.radioInputs.forEach(input => input.checked = false);
      
      //  radioInput.checked = true;
      
      //  radioInput.dispatchEvent(new Event('change', { bubbles: true }));

      const nextElement = radioInput.nextElementSibling;
      nextElement?.click();
      
      
      const formLabel = this.variantSelects.querySelector('.form__label__value');
      if (formLabel) {
        formLabel.textContent = variant.option1;
      }

      // Revalidate after variant selection
      this.validateSelections();
    } else {
      console.log('No matching radio input found for variant:', variant.option1);
    }
  }
}

customElements.define('product-size-selector', ProductSizeSelector);